import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { ExternalLink, RefreshCw } from 'lucide-react';
import CouponCard from '../components/CouponCard';
import ProductCard from '../components/ProductCard';
import CouponFinder from '../components/CouponFinder';
import PageBanner from '../components/PageBanner';
import { Skeleton } from '../components/ui/skeleton';
import { useSite } from '../context/SiteContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Cupones = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [forceSortByCopies, setForceSortByCopies] = useState(false);
  const [finderResetSignal, setFinderResetSignal] = useState(0);
  const { settings, getImageUrl, darkMode } = useSite();
  const lastRefresh = useRef(0);

  const fetchCoupons = async (showRefreshing = false, overrideSort = null, force = false) => {
    // Prevent multiple rapid refreshes (minimum 2 seconds between refreshes)
    // — unless it's a user-initiated action (force=true)
    const now = Date.now();
    if (!force && now - lastRefresh.current < 2000 && !loading) {
      return;
    }
    lastRefresh.current = now;
    
    if (showRefreshing) setRefreshing(true);
    try {
      const response = await axios.get(`${API}/coupons?_t=${Date.now()}`);
      const nowDate = new Date();
      // Keep ALL coupons (including expired/sold-out) so they can be shown grayed out
      // as "Cupón agotado" instead of disappearing.
      let activeCoupons = [...response.data];
      
      // Sort: by default show newest first. Only "copies" if user clicked refresh (forceSortByCopies) or explicit override.
      const sortBy = overrideSort || (forceSortByCopies ? 'copies' : 'recent');
      switch (sortBy) {
        case 'copies':
          activeCoupons.sort((a, b) => (b.clicks || 0) - (a.clicks || 0));
          break;
        case 'votes':
          activeCoupons.sort((a, b) => (b.worked_count || 0) - (a.worked_count || 0));
          break;
        case 'trust':
          activeCoupons.sort((a, b) => {
            const trustA = (a.worked_count || 0) / Math.max((a.worked_count || 0) + (a.not_worked_count || 0), 1);
            const trustB = (b.worked_count || 0) / Math.max((b.worked_count || 0) + (b.not_worked_count || 0), 1);
            return trustB - trustA;
          });
          break;
        case 'recent':
        default:
          activeCoupons.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
          break;
      }

      // Push unavailable coupons (expired OR sold out) to the end, keeping available first.
      const isUnavailable = (c) => {
        const expired = c.expiration_date && new Date(c.expiration_date) < nowDate;
        const soldOut = c.stock !== null && c.stock !== undefined && c.stock <= 0;
        return expired || soldOut;
      };
      activeCoupons.sort((a, b) => (isUnavailable(a) ? 1 : 0) - (isUnavailable(b) ? 1 : 0));

      setCoupons(activeCoupons);
    } catch (error) {
      console.error('Error fetching coupons:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchCoupons();
  }, []);

  // Refresh when settings change
  useEffect(() => {
    if (!loading) {
      fetchCoupons();
    }
  }, [settings.coupon_sort_by]);

  const handleRefresh = () => {
    // Reset everything: hide finder result, back to "Todos" tab, sort by most copied
    setForceSortByCopies(true);
    setActiveTab('all');
    setFinderResetSignal(s => s + 1);
    // Scroll to top for a full-page reload feel
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchCoupons(true, 'copies', true);
  };

  const handleCouponUpdate = (couponId, updates) => {
    setCoupons(prev => prev.map(c => 
      c.id === couponId ? { ...c, ...updates } : c
    ));
  };

  // Coupon tabs (dynamic, configurable from admin)
  const DEFAULT_TABS = [
    { id: 'store', emoji: '🛒', name: 'Tienda', color: '#FFD600', text_color: '#0f172a' },
    { id: 'bank', emoji: '🏦', name: 'Bancarios', color: '#2962FF', text_color: '#ffffff' }
  ];
  let couponTabs = DEFAULT_TABS;
  try {
    const parsedTabs = JSON.parse(settings.coupon_tabs || '[]');
    if (parsedTabs.length > 0) couponTabs = parsedTabs;
  } catch (e) {
    couponTabs = DEFAULT_TABS;
  }
  // Ensure both default tabs (store + bank) always exist so the buttons never disappear
  const ensureTab = (id) => {
    if (!couponTabs.some(t => t.id === id)) {
      const def = DEFAULT_TABS.find(t => t.id === id);
      if (def) couponTabs = [...couponTabs, def];
    }
  };
  ensureTab('store');
  ensureTab('bank');
  const tabIds = couponTabs.map(t => t.id);
  const currentTab = couponTabs.find(t => t.id === activeTab) || couponTabs[0];
  const couponsForTab = (tabId) => {
    // Always exclude product-kind items (those go in Ofertas)
    const coupsOnly = coupons.filter(c => (c.item_kind || 'coupon') === 'coupon');
    if (tabId === 'all') {
      // Show only bank and store coupons (no other tabs)
      return coupsOnly.filter(c => {
        const t = c.coupon_type || 'store';
        return t === 'bank' || t === 'store';
      });
    }
    // STRICT match: each tab shows only coupons of its exact type
    return coupsOnly.filter(c => (c.coupon_type || 'store') === tabId);
  };
  const displayedCoupons = activeTab === 'all' ? couponsForTab('all') : couponsForTab(currentTab.id);

  // Count of AVAILABLE coupons (not expired / not sold out) per tab, for the badge on filter buttons
  const isCouponUnavailable = (c) => {
    const expired = c.expiration_date && new Date(c.expiration_date) < new Date();
    const soldOut = c.stock !== null && c.stock !== undefined && c.stock <= 0;
    return expired || soldOut;
  };
  const availableCount = (tabId) => couponsForTab(tabId).filter(c => !isCouponUnavailable(c)).length;

  const handleFilterClick = (tabId) => {
    setActiveTab(tabId);
    // Filter buttons show newest first (recent). Only the refresh button forces sort by copies.
    setForceSortByCopies(false);
    fetchCoupons(true, 'recent', true);
  };


  // Dynamic grid classes - Force 1 column on mobile for better card display
  const getGridClasses = () => {
    const desktop = settings.grid_desktop_columns || 3;
    const tablet = settings.grid_tablet_columns || 2;
    const mobile = settings.grid_mobile_columns || 1; // Default to 1 column on mobile
    
    const mobileClass = mobile === 1 ? 'grid-cols-1' : 'grid-cols-2';
    const tabletClass = tablet === 1 ? 'sm:grid-cols-1' : tablet === 2 ? 'sm:grid-cols-2' : 'sm:grid-cols-3';
    const desktopClass = desktop === 1 ? 'lg:grid-cols-1' : desktop === 2 ? 'lg:grid-cols-2' : desktop === 3 ? 'lg:grid-cols-3' : 'lg:grid-cols-4';
    
    return `${mobileClass} ${tabletClass} ${desktopClass}`;
  };

  // Banner height classes
  const getBannerHeightClass = () => {
    const height = settings.banner_height || 'auto';
    if (height === 'small') return 'py-1.5 sm:py-3';
    if (height === 'medium') return 'py-2 sm:py-6';
    if (height === 'large') return 'py-3 sm:py-10';
    return 'py-2 sm:py-6';
  };

  const bannerStyle = {
    background: `linear-gradient(to bottom right, ${settings.banner_gradient_from || '#fbbf24'}, ${settings.banner_gradient_to || '#f59e0b'})`
  };

  // Dynamic date: replace {fecha} in banner texts with today's date (updates daily automatically)
  const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const hoy = new Date();
  const fechaHoy = `${hoy.getDate()} de ${MESES[hoy.getMonth()]}`;
  const conFecha = (t) => (t || '').replace(/{fecha}/gi, fechaHoy);

  const darkCardBg = settings.dark_card_bg_color || '#1e293b';
  const darkText = settings.dark_text_color || '#f1f5f9';

  return (
    <div className={`min-h-screen pb-20 ${!darkMode ? 'bg-slate-50' : ''}`} style={darkMode ? { backgroundColor: 'transparent' } : {}} data-testid="cupones-page">
      <PageBanner />

      {/* Coupon Finder (calculator) - right below banner, no gap */}
      <CouponFinder resetSignal={finderResetSignal} />

      {/* Filter buttons: Todos / Tienda / Bancarios + Refresh */}
      <div className="flex items-center gap-2.5 mb-4 pt-2 overflow-x-auto overflow-y-visible scrollbar-hide" data-testid="filter-buttons-row">
        {[{ id: 'all', label: 'Todos' }, ...couponTabs.map(t => ({ id: t.id, label: t.name }))].map((btn) => {
          const isActive = activeTab === btn.id;
          const count = availableCount(btn.id);
          return (
            <button
              key={btn.id}
              onClick={() => handleFilterClick(btn.id)}
              data-testid={`filter-btn-${btn.id}`}
              className={`relative px-5 sm:px-6 py-2 rounded-full text-sm sm:text-base font-bold whitespace-nowrap transition-all active:scale-95 border-2 ${
                isActive
                  ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
                  : darkMode
                    ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700'
                    : 'bg-white border-neutral-200 text-slate-800 hover:bg-neutral-50'
              }`}
            >
              <span>{btn.label}</span>
              {count > 0 && (
                <span
                  data-testid={`filter-count-${btn.id}`}
                  className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 inline-flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-extrabold leading-none shadow-md ring-2 ring-white"
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
        {/* Refresh button - sorts by most copied */}
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          data-testid="filter-btn-refresh"
          aria-label="Actualizar y ordenar por más copiados"
          title="Ordenar por más copiados"
          className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all active:scale-95 border-2 ${
            forceSortByCopies
              ? 'bg-blue-600 border-blue-600 text-white shadow-sm'
              : darkMode
                ? 'bg-slate-800 border-slate-700 text-slate-200 hover:bg-slate-700'
                : 'bg-white border-neutral-200 text-slate-800 hover:bg-neutral-50'
          } disabled:opacity-70`}
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Coupons Grid */}
      {loading ? (
        <div className={`grid gap-2 sm:gap-3 ${getGridClasses()}`}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border-2 border-slate-200 p-2 flex gap-2">
              <Skeleton className="h-16 w-16 rounded-lg" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-3 w-16" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-6 w-20 rounded" />
              </div>
            </div>
          ))}
        </div>
      ) : displayedCoupons.length > 0 ? (
        <div className={`grid gap-2 sm:gap-3 ${getGridClasses()}`}>
          {displayedCoupons.map((coupon, index) => (
            <div 
              key={coupon.id} 
              className="animate-in fade-in slide-in-from-bottom-2 rounded-[22px]"
              style={{ 
                animationDelay: `${index * 40}ms`, 
                animationFillMode: 'both'
              }}
            >
              {coupon.item_kind === 'product' ? (
                <ProductCard 
                  coupon={coupon} 
                  onUpdate={(updates) => handleCouponUpdate(coupon.id, updates)}
                />
              ) : (
                <CouponCard 
                  coupon={coupon} 
                  onUpdate={(updates) => handleCouponUpdate(coupon.id, updates)}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className={`text-center py-8 sm:py-12 rounded-xl sm:rounded-2xl shadow-sm border ${darkMode ? 'border-slate-700' : 'bg-white border-slate-100'}`} style={darkMode ? { backgroundColor: darkCardBg } : {}}>
          <div className={`w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center mx-auto mb-3 ${darkMode ? 'bg-slate-700' : 'bg-slate-100'}`}>
            <ExternalLink className="w-6 h-6 sm:w-8 sm:h-8 text-slate-400" />
          </div>
          <h3 className="font-fredoka text-base sm:text-lg font-semibold mb-1" style={{ color: darkMode ? darkText : '#334155' }}>
            {tabIds.indexOf(currentTab.id) === 0 ? (settings.msg_no_coupons || 'No hay cupones disponibles') : `No hay cupones de ${currentTab.name} disponibles`}
          </h3>
          <p className={`text-xs sm:text-sm max-w-sm mx-auto px-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            {tabIds.indexOf(currentTab.id) === 0 ? (settings.msg_no_coupons_desc || 'Pronto agregaremos nuevos cupones de descuento. ¡Vuelve pronto!') : `Pronto agregaremos cupones de ${currentTab.name}. ¡Vuelve pronto!`}
          </p>
        </div>
      )}
    </div>
  );
};

export default Cupones;
